import React, { useEffect, useMemo, useState } from 'react';

function normalizePath(value) {
  return (value || '').replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').trim();
}

function isValidAccountNumber(value) {
  if (!value) return true;
  return /^(x\d{4}|Mixed|NONE)$/i.test(value.trim());
}

export default function BulkApprovePushModal({
  isOpen,
  onClose,
  files,
  docTypesData,
  allSubTypes,
  onPreview,
  onSubmit,
  isSubmitting,
}) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setRows(
      (files || []).map((file) => ({
        fileId: file.fileId,
        originalName: file.file?.name || '',
        suggestedName: (file.acceptedName || file.suggestedName || file.file?.name || '').trim(),
        docType: file.docType || '',
        subType: file.subType || '',
        accountNumber: file.accountNumber || '',
        suggestedFolder: file.suggestedFolder || '',
        selected: true,
        source: file,
      }))
    );
  }, [isOpen, files]);

  const selectedCount = useMemo(() => rows.filter((r) => r.selected).length, [rows]);

  const allSelected = useMemo(() => rows.length > 0 && rows.every((r) => r.selected), [rows]);

const validationError = useMemo(() => {
  const selectedRows = rows.filter((r) => r.selected);
  if (selectedRows.length === 0) return 'Select at least one row.';
  for (const row of selectedRows) {
    const filename = (row.suggestedName || '').trim();
    if (!filename) {
      return `Suggested filename is required for ${row.originalName || row.fileId}.`;
    }
    if (/[\[\]\/\\]/.test(filename)) {
      return `Filename cannot contain brackets ([ or ]) or slashes (/ or \\) for ${row.originalName || row.fileId}.`;
    }
    if (!isValidAccountNumber(row.accountNumber || '')) {
      return `Invalid account number for ${row.originalName || row.fileId}. Use x1234, Mixed, or NONE.`;
    }
  }
  return '';
}, [rows]);

  const handleToggleAll = (checked) => {
    setRows((prev) => prev.map((row) => ({ ...row, selected: checked })));
  };

  const handleRowChange = (fileId, updates) => {
    setRows((prev) => prev.map((row) => (row.fileId === fileId ? { ...row, ...updates } : row)));
  };

  const getSubTypesForDocType = (docTypeId) => {
    if (!docTypeId || !Array.isArray(docTypesData) || docTypesData.length === 0) {
      return allSubTypes || [];
    }
    const docType = docTypesData.find((dt) => dt.id === docTypeId);
    if (!docType || !Array.isArray(docType.sub_types) || docType.sub_types.length === 0) {
      return [];
    }
    return docType.sub_types.map((st) => ({
      id: typeof st === 'object' ? st.id : '',
      name: typeof st === 'object' ? st.name : st,
    }));
  };

  const handleSubmit = async () => {
    if (validationError) return;
    const payload = rows
      .filter((r) => r.selected)
      .map((r) => ({
        fileId: r.fileId,
        suggestedName: normalizePath(r.suggestedName).replace(/\//g, ' '),
        docType: r.docType || '',
        subType: r.subType || '',
        accountNumber: (r.accountNumber || '').trim(),
        suggestedFolder: r.suggestedFolder || '',
        source: r.source,
      }));
    await onSubmit(payload);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Bulk Review and Push</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting}></button>
            </div>
            <div className="modal-body">
              <div className="table-responsive">
                <table className="table table-sm table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '20px' }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={(e) => handleToggleAll(e.target.checked)}
                          disabled={isSubmitting || rows.length === 0}
                        />
                      </th>
                      <th>Original Filename</th>
                      <th style={{ width: '35%' }}>Suggested Filename</th>
                      <th>Doc Type</th>
                      <th>Sub Type</th>
                      <th style={{ width: '85px' }}>Account #</th>
                      <th>Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const subTypeOptions = getSubTypesForDocType(row.docType);
                      return (
                        <tr key={row.fileId} className={row.selected ? '' : 'table-light'}>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.selected}
                              onChange={(e) => handleRowChange(row.fileId, { selected: e.target.checked })}
                              disabled={isSubmitting}
                            />
                          </td>
                          <td>{row.originalName || '-'}</td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={row.suggestedName}
                              onChange={(e) => handleRowChange(row.fileId, { suggestedName: e.target.value })}
                              disabled={isSubmitting}
                            />
                          </td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={row.docType || ''}
                              onChange={(e) => {
                                const nextDocType = e.target.value;
                                const nextSubTypeOptions = getSubTypesForDocType(nextDocType);
                                const isCurrentSubTypeValid = nextSubTypeOptions.some((opt) => opt.id === row.subType);
                                handleRowChange(row.fileId, {
                                  docType: nextDocType,
                                  subType: isCurrentSubTypeValid ? row.subType : '',
                                });
                              }}
                              disabled={isSubmitting}
                            >
                              <option value="">Select...</option>
                              {(docTypesData || []).map((dt) => (
                                <option key={dt.id} value={dt.id}>
                                  {dt.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={row.subType || ''}
                              onChange={(e) => handleRowChange(row.fileId, { subType: e.target.value })}
                              disabled={isSubmitting}
                            >
                              <option value="">Select...</option>
                              {subTypeOptions.map((st) => {
                                if (typeof st === 'string') {
                                  return (
                                    <option key={st} value={st}>
                                      {st}
                                    </option>
                                  );
                                }
                                return (
                                  <option key={st.id || st.name} value={st.id || st.name}>
                                    {st.name}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              className={`form-control form-control-sm ${
                                row.accountNumber && !isValidAccountNumber(row.accountNumber) ? 'is-invalid' : ''
                              }`}
                              value={row.accountNumber}
                              onChange={(e) => handleRowChange(row.fileId, { accountNumber: e.target.value })}
                              disabled={isSubmitting}
                              placeholder="x1234 | Mixed | NONE"
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0"
                              onClick={() => onPreview(row.source)}
                              disabled={isSubmitting}
                            >
                              Open
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">{selectedCount} selected</small>
                {validationError && <small className="text-danger">{validationError}</small>}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={isSubmitting || !!validationError}
              >
                {isSubmitting ? 'Approving and Pushing...' : `Approve and Push Selected (${selectedCount})`}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}
